import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * @typedef {object} TableRowHoverState
 * @property {TableRowFlattened | null} row
 * @property {HTMLTableRowElement | null} element
 */

/**
 * @typedef {object} TableRowHoverDispatchValue
 * @property {(row: TableRowFlattened, element: HTMLTableRowElement) => void} setHoveredRow
 * @property {() => void} clearHoveredRow
 * @property {(locked: boolean) => void} setHoveredRowLocked
 */

/** @type {React.Context<TableRowHoverState | undefined>} */
const TableRowHoverStateContext = createContext(undefined);

/** @type {React.Context<TableRowHoverDispatchValue | null>} */
const TableRowHoverDispatchContext = createContext(null);

const emptyDispatch = Object.freeze({
  /** @type {(row: TableRowFlattened, element: HTMLTableRowElement) => void} */
  setHoveredRow: () => {},
  clearHoveredRow: () => {},
  /** @type {(locked: boolean) => void} */
  setHoveredRowLocked: () => {},
});

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export function TableRowHoverProvider({ children }) {
  const [hoverState, setHoverState] = useState(/** @type {TableRowHoverState} */ ({
    row: null,
    element: null,
  }));
  const lockedRef = useRef(false);

  const setHoveredRow = useCallback((row, element) => {
    setHoverState({ row, element });
  }, []);

  const clearHoveredRow = useCallback(() => {
    if (lockedRef.current) return;
    setHoverState({ row: null, element: null });
  }, []);

  const setHoveredRowLocked = useCallback((locked) => {
    lockedRef.current = locked;
  }, []);

  const dispatchValue = useMemo(
    () => ({
      setHoveredRow,
      clearHoveredRow,
      setHoveredRowLocked,
    }),
    [setHoveredRow, clearHoveredRow, setHoveredRowLocked],
  );

  return (
    <TableRowHoverDispatchContext.Provider value={dispatchValue}>
      <TableRowHoverStateContext.Provider value={hoverState}>
        {children}
      </TableRowHoverStateContext.Provider>
    </TableRowHoverDispatchContext.Provider>
  );
}

/**
 * @returns {TableRowHoverState}
 */
export function useTableRowHoverStateOptional() {
  const state = useContext(TableRowHoverStateContext);
  if (state === undefined) {
    return { row: null, element: null };
  }
  return state;
}

/**
 * @returns {TableRowHoverDispatchValue}
 */
export function useTableRowHoverDispatchOptional() {
  const ctx = useContext(TableRowHoverDispatchContext);
  return ctx ?? emptyDispatch;
}
